import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../stores/appStore';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Plus, AppWindow, Edit2, Copy, Key, ShieldCheck, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { formatDate, getOAuth2AuthorizeUrl, getOAuth2TokenUrl } from '../../lib/utils';
import { toast } from '../../components/ui/toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export function ApplicationList() {
  const { applications, loading, error, fetchApplications, clearError, fetchOrganizations, organizations } = useAppStore();
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState<string | null>(null);
  const initialized = useRef(false);

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    // Load organizations for super admin selector
    if (isSuperAdmin()) {
      fetchOrganizations().catch(() => { });
    }
  }, [isSuperAdmin, fetchOrganizations]);

  useEffect(() => {
    if (!user || initialized.current) return;

    clearError();

    // Auto-select organization based on user role
    if (isSuperAdmin()) {
      // Super admin - no default selection, wait for user to choose
      initialized.current = true;
    } else if (user.role === 'admin' && user.org_id) {
      // Org admin - auto-select their organization
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setOrgId(user.org_id);
      fetchApplications(user.org_id).catch((error) => {
        console.error('Failed to fetch applications:', error);
      });
      initialized.current = true;
    }
  }, [user, isSuperAdmin, clearError, fetchApplications]);

  // Auto-select first organization for super admin when organizations are loaded
  useEffect(() => {
    if (isSuperAdmin() && organizations.length > 0 && !orgId) {
      setOrgId(organizations[0].id);
      fetchApplications(organizations[0].id).catch((error) => {
        console.error('Failed to fetch applications:', error);
      });
    }
  }, [organizations, isSuperAdmin, orgId, fetchApplications]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const handleEditClick = (appId: string) => {
    // Pass orgId via navigation state
    if (orgId) {
      navigate(`/dashboard/apps/${appId}`, { state: { orgId } });
    }
  };

  const handleOrgSelect = (value: string) => {
    setOrgId(value);
    fetchApplications(value).catch((error) => {
      console.error('Failed to fetch applications:', error);
    });
  };

  const handleCreateClick = () => {
    if (orgId) {
      navigate('/dashboard/apps/create', { state: { orgId } });
    } else {
      navigate('/dashboard/apps/create');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Applications</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Manage OAuth2 applications</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateClick} disabled={isSuperAdmin() && !orgId}>
            <Plus className="h-4 w-4 mr-2" />
            Create Application
          </Button>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isSuperAdmin() && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Note:</strong> Super admins can view applications for all organizations.
                Select an organization to view its applications.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Organization:</span>
              <Select value={orgId || ''} onValueChange={handleOrgSelect}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select an organization...">
                    {orgId && organizations.find(o => o.id === orgId)?.name}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {!isSuperAdmin() && !orgId && user?.role === 'admin' && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Loading your organization...
            </p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size={48} />
        </div>
      ) : !orgId && isSuperAdmin() ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AppWindow className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Select an Organization
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Please select an organization from above to view its applications
            </p>
          </CardContent>
        </Card>
      ) : applications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AppWindow className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Applications Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first OAuth2 application to get started
            </p>
            <Button onClick={handleCreateClick}>
              Create Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {applications.map((app) => (
            <Card key={app.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AppWindow className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{app.name}</CardTitle>
                    <Badge
                      className={app.is_active ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                    >
                      {app.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditClick(app.id)}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Client ID:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs font-mono">
                        {app.client_id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(app.client_id, 'Client ID')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Client Secret:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs font-mono">
                        ••••••••••••
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(app.client_secret, 'Client Secret')}
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Organization:</span>
                    <span className="ml-2 font-medium">{app.organization_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created:</span>
                    <span className="ml-2">{formatDate(app.created_at)}</span>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Redirect URIs:</span>
                  <div className="mt-2 space-y-1">
                    {app.redirect_uris.map((uri, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-green-600" />
                        <code className="text-xs font-mono text-gray-700 dark:text-gray-300">{uri}</code>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      Authorize URL:
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(
                        getOAuth2AuthorizeUrl(app.client_id, app.redirect_uris[0] || '', app.scope || undefined),
                        'Authorize URL'
                      )}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <code className="block bg-gray-50 dark:bg-gray-900 px-2 py-1.5 rounded text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                    {getOAuth2AuthorizeUrl(app.client_id, app.redirect_uris[0] || '', app.scope || undefined)}
                  </code>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      Token URL:
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(getOAuth2TokenUrl(), 'Token URL')}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <code className="block bg-gray-50 dark:bg-gray-900 px-2 py-1.5 rounded text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                    {getOAuth2TokenUrl()}
                  </code>
                </div>

                {app.app_url && (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-400">App URL:</span>
                    <a
                      href={app.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {app.app_url}
                    </a>
                  </div>
                )}

                {app.scope && app.scope.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {app.scope.map((s, idx) => {
                      const colors = [
                        'bg-blue-600 hover:bg-blue-700',
                        'bg-purple-600 hover:bg-purple-700',
                        'bg-pink-600 hover:bg-pink-700',
                        'bg-indigo-600 hover:bg-indigo-700',
                        'bg-teal-600 hover:bg-teal-700',
                        'bg-orange-600 hover:bg-orange-700',
                      ];
                      const colorClass = colors[idx % colors.length];
                      return (
                        <Badge key={s} className={`${colorClass} text-xs`}>
                          {s}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
